// Single source of truth for page titles, descriptions and crawlable summary text.
// Used by <SEO /> at runtime and by scripts/prerender.mjs at build time.
import { programs } from './programsData';

export const SITE_URL = 'https://patrickseducare.com';
export const BRAND = "Patrick's Edu Care";

const withBrand = (t) => (`${t} | ${BRAND}`.length <= 60 ? `${t} | ${BRAND}` : t);

const STATIC_PAGES = {
  '/': {
    title: "Study Abroad Consultants in Kochi | Patrick's Edu Care",
    description: 'Study abroad with Patrick\'s Edu Care, Kochi. MBBS, Nursing, AI, MBA and more in Georgia, Bulgaria, Moldova, Malta, Germany and the UK. Free counselling.',
    h1: 'Study Abroad with Patrick\'s Edu Care',
    body: [
      "Patrick's Edu Care is a study abroad consultancy based in Ernakulam, Kochi, Kerala. We guide Indian students and parents through every step of studying abroad: choosing the right course and country, shortlisting universities, admission paperwork, student visa, travel and arrival support.",
      'Popular programs include MBBS, BSc Nursing, Dentistry, Veterinary Medicine, Artificial Intelligence, Cybersecurity, BBA and MBA. Our destinations include Georgia, Bulgaria, Moldova, Malta, Germany, the UK, France and Austria.',
      'Your first one-to-one counselling session with Dr. Patrick and our team is free. Talk to us on WhatsApp, call +91 75948 81898, or visit our office in Ravipuram, Ernakulam.',
    ],
  },
  '/about': {
    title: "About Us | Study Abroad Experts | Patrick's Edu Care",
    description: "Meet Dr. Patrick and the Patrick's Edu Care team in Kochi, Kerala. Years of helping Indian students secure admissions at universities across Europe.",
    h1: "About Patrick's Edu Care",
    body: [
      "Patrick's Edu Care was founded with a simple promise: quality international education should be honest, affordable and well guided. Led by Dr. Patrick, our Kochi team has helped thousands of Indian students move abroad for medicine, nursing, technology and business degrees.",
      'We visit our partner universities in person, stay in touch with students after they land, and keep parents informed at every stage, from the first counselling call to graduation.',
    ],
  },
  '/services': {
    title: "Our Services | Admission, Visa & Travel | Patrick's Edu Care",
    description: 'Career counselling, university shortlisting, admission paperwork, student visa, education loans, travel and arrival support for studying abroad from India.',
    h1: 'Study Abroad Services',
    body: [
      'We handle the full study abroad journey: one-to-one career counselling, course and country selection, university shortlisting, application and admission letters, document attestation and apostille, student visa filing and interview preparation.',
      'We also help with education loan guidance, forex, flight booking, pre-departure briefings, airport pick-up, hostel allocation and on-ground support after arrival.',
    ],
  },
  '/programs': {
    title: "Study Abroad Programs: MBBS, Nursing, AI & MBA",
    description: 'Compare MBBS, BSc Nursing, Dentistry, Veterinary, AI, Cybersecurity, BBA and MBA programs abroad: fees, duration, countries and partner universities.',
    h1: 'Study Abroad Programs',
    body: [
      'Explore every program we offer, with tuition fees, course duration, medium of instruction and the countries and universities where you can study it.',
    ],
  },
  '/success-stories': {
    title: "Student Success Stories | Patrick's Edu Care",
    description: "Real Indian students who studied MBBS, Nursing, AI and MBA abroad with Patrick's Edu Care. Read their journeys, outcomes and what their parents say.",
    h1: 'Student Success Stories',
    body: [
      'Behind every admission letter is a family that trusted us. Read how students from Kerala and across India secured admissions at universities in Europe and Central Asia, and where they are today.',
    ],
  },
  '/gallery': {
    title: "Photo Gallery: Students & Campus Visits | Patrick's Edu Care",
    description: "Photos of Patrick's Edu Care students, university campus visits, send-off events and Dr. Patrick meeting students abroad in Georgia, Bulgaria and more.",
    h1: 'Gallery',
    body: [
      'Moments from our students\' journeys: airport send-offs, campus visits, graduation days and Dr. Patrick\'s visits to our partner universities across Europe.',
    ],
  },
  '/contact': {
    title: "Contact Us | Free Study Abroad Counselling in Kochi",
    description: "Contact Patrick's Edu Care in Ravipuram, Ernakulam. Call +91 75948 81898, WhatsApp, email or book a free video consultation. Open Monday to Saturday.",
    h1: "Contact Patrick's Edu Care",
    body: [
      'Book a free one-to-one video consultation with Dr. Patrick, or reach us on WhatsApp, phone or email. We usually reply to messages within 24 hours.',
      'Office: 2nd Floor, Parur TK Building, M.G. Road, Ravipuram, Ernakulam, Kerala 682016. Office hours: Monday to Saturday, 10 AM to 6 PM IST. Phone: +91 75948 81898. Email: info@patrickseducare.com.',
      'Not sure where to start? Tell us your marks, budget and preferred course, and we will suggest suitable countries and universities in the first call. Walk-ins are welcome, but booking ahead ensures a counsellor is free for you.',
    ],
    faqs: [
      { q: 'Is the first counselling session free?', a: 'Yes. Your first one-to-one counselling session, in person or by video call, is completely free.' },
      { q: 'How quickly will you reply?', a: 'We reply to WhatsApp messages and emails within 24 hours, Monday to Saturday.' },
      { q: 'Can parents join the consultation?', a: 'Yes. We encourage parents to join so that fees, safety and the admission timeline are clear to the whole family.' },
      { q: 'What should I bring to the office?', a: 'Your 10th and 12th mark sheets, passport (if available) and NEET scorecard for medical courses.' },
    ],
  },
  '/ausbildung': {
    title: "Ausbildung in Germany: Paid Training | Patrick's Edu Care",
    description: 'Ausbildung in Germany for Indian students: paid vocational training in nursing, hospitality and technical trades, with German language support and visa help.',
    h1: 'Ausbildung in Germany',
    body: [
      'Ausbildung is Germany\'s dual vocational training system. You train in a company and study at a vocational school at the same time, and you earn a monthly stipend from day one.',
      'We help with German language preparation, finding a training place, documents, the visa process and arrival in Germany.',
    ],
  },
  '/privacy-policy': {
    title: "Privacy Policy | Patrick's Edu Care",
    description: "How Patrick's Edu Care collects, uses and protects the personal information you share through our website, enquiry forms and counselling sessions.",
    h1: 'Privacy Policy',
    body: [
      'This policy explains what information we collect when you contact us or fill in a form, how we use it to provide counselling and admission support, and how we keep it secure.',
    ],
  },
};

const STUDY_IN = {
  georgia: { name: 'Georgia', desc: 'Study in Georgia: MBBS, Nursing, Dentistry, AI and MBA in English. Universities, tuition fees, living costs and admission help for Indian students.' },
  moldova: { name: 'Moldova', desc: 'Study in Moldova: MBBS, Veterinary, BBA and MBA. Government hostels, tuition and living costs in euros, and admission support for Indian students.' },
  bulgaria: { name: 'Bulgaria', desc: 'Study in Bulgaria, an EU country: MBBS, Nursing, Veterinary, AI and Cybersecurity in Sofia, Plovdiv and Varna. Fees, universities and admissions.' },
  malta: { name: 'Malta', desc: "Study in Malta, Europe's English-speaking island: Nursing, AI, Cybersecurity and business courses, EU degrees, part-time work and post-study stay." },
  germany: { name: 'Germany', desc: 'Study in Germany: free public universities, English-taught masters, Ausbildung, 18-month post-study visa and EU Blue Card. Admission help for Indians.' },
  uk: { name: 'the UK', short: 'UK', desc: 'Study in the UK: one-year masters, Russell Group universities, Graduate Route work visa, tuition fees, living costs and admission help for Indian students.' },
  france: { name: 'France', desc: 'Study in France: English-taught masters, low public university fees, top business schools, post-study work permit and admission help for Indian students.' },
  austria: { name: 'Austria', desc: 'Study in Austria: about EUR 1,453 a year at public universities, Vienna quality of life, part-time work and the Red-White-Red Card path for Indians.' },
};

const studyInMeta = (slug) => {
  const c = STUDY_IN[slug];
  if (!c) return null;
  const label = c.short || c.name;
  return {
    title: withBrand(`Study in ${label} from India 2026`),
    description: c.desc,
    h1: `Study in ${c.name}`,
    body: [c.desc],
    faqsKey: slug,
  };
};

const programMeta = (p) => ({
  title: withBrand(`${p.name} Abroad 2026: Fees & Countries`),
  description: `${p.fullName} abroad: ${p.duration}, ${p.medium} medium, from ${p.averageFee}. Compare ${p.countries.map((c) => c.name).join(', ')}.`.slice(0, 160),
  h1: `${p.name} Abroad`,
  body: [p.longDescription, ...(p.highlights || [])],
  links: p.countries.map((c) => ({ href: `/programs/${p.slug}/${c.slug}`, label: `${p.name} in ${c.name}` })),
  program: p,
});

const countryProgramMeta = (p, c) => ({
  title: [`${p.name} in ${c.name} 2026: Fees & Universities`, `${p.name} in ${c.name}: Fees & Universities`].map(withBrand).find((t) => t.length <= 60) || `${p.name} in ${c.name} 2026`,
  description: `${p.name} in ${c.name}: tuition fees, eligibility and intake for ${c.universities.length} ${c.universities.length === 1 ? 'university' : 'universities'}. Living cost ${c.livingCost}. Free counselling.`.slice(0, 160),
  h1: `${p.name} in ${c.name}`,
  body: [
    c.about,
    ...(c.whyStudy || []),
    ...c.universities.map((u) => `${u.name}, ${u.location}. Tuition: ${u.tuitionFee}. Duration: ${u.duration}. Medium: ${u.medium}. Intake: ${u.intake}.`),
  ],
  links: [{ href: `/programs/${p.slug}`, label: `All ${p.name} countries` }],
  program: p,
  country: c,
});

export const getSeoMeta = (path) => {
  const clean = (path || '/').replace(/\/+$/, '') || '/';
  if (STATIC_PAGES[clean]) return STATIC_PAGES[clean];
  const study = clean.match(/^\/study-in-([a-z-]+)$/);
  if (study) return studyInMeta(study[1]);
  const prog = clean.match(/^\/programs\/([a-z-]+)(?:\/([a-z-]+))?$/);
  if (prog) {
    const p = programs.find((x) => x.slug === prog[1]);
    if (!p) return null;
    if (!prog[2]) return programMeta(p);
    const c = p.countries.find((x) => x.slug === prog[2]);
    return c ? countryProgramMeta(p, c) : null;
  }
  return null;
};

export const getAllSeoRoutes = () => [
  ...Object.keys(STATIC_PAGES),
  ...Object.keys(STUDY_IN).map((s) => `/study-in-${s}`),
  ...programs.flatMap((p) => [`/programs/${p.slug}`, ...p.countries.map((c) => `/programs/${p.slug}/${c.slug}`)]),
];
